import { type ComponentType, memo, type ReactNode, useCallback } from 'react';

import { type FiltersTrackingLocation, useTrackFiltersRemoved } from '@air/analytics';
import { MinusIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';

import { type FilterSelectProps } from '~/components/FiltersMenu/FilterSelect';
import {
  baseFiltersOptions,
  type CustomFieldFilterType,
  type FilterSelectType,
  type FilterType,
} from '~/components/FiltersMenu/types';
import { customFieldIconMap } from '~/constants/customFieldIconMap';
import { FILTER_CARD } from '~/constants/testIDs';
import { type SelectedFilter } from '~/store/filters/types';
import { filterTypeToTrackingType } from '~/store/filters/utils';

export type BaseFilterCardProps = {
  onFilterTypeChange?: FilterSelectType;
  getFiltersOptions: FilterSelectProps['getFiltersOptions'];
  trackLocation?: FiltersTrackingLocation;
};

export type FilterCardProps = {
  onClearClick?: () => void;
  children: ReactNode;
  currentFilter: FilterType;
  currentCustomField?: CustomFieldFilterType;
  testId?: string;
  onRemoveFilter: (filter: SelectedFilter) => void;
} & BaseFilterCardProps;

type FilterCardTitleIcon = ComponentType<{ className?: string }>;

export const FilterCard = memo(
  ({ children, currentFilter, currentCustomField, trackLocation, onRemoveFilter }: FilterCardProps) => {
    const { trackFiltersRemoved } = useTrackFiltersRemoved();
    /**
     * @TODO - Update this testid to not be dynamic based on customField name or currentFilter.
     */
    const testId = currentCustomField?.id
      ? `${FILTER_CARD}-${currentCustomField.name}`
      : `${FILTER_CARD}-${currentFilter}`;

    const onFilterDelete = useCallback(() => {
      const filter = { type: currentFilter, customField: currentCustomField };
      onRemoveFilter(filter);

      trackFiltersRemoved({
        types: filterTypeToTrackingType(filter.type),
        location: trackLocation,
        builderType: 'filter-builder',
      });
    }, [currentCustomField, currentFilter, onRemoveFilter, trackLocation, trackFiltersRemoved]);

    const filter = baseFiltersOptions[currentFilter];
    const title = currentCustomField?.name ?? filter.label;
    const Icon: FilterCardTitleIcon | undefined = currentCustomField?.type.title
      ? customFieldIconMap[currentCustomField.type.title]
      : filter.icon;

    return (
      <div
        className="flex flex-col gap-2 border-b ads:border-b-ads-gray-5 border-b-gray-5 px-2 py-3 last:border-0 md:mx-3 md:px-1 md:py-3"
        data-testid={testId}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            {!!Icon && (
              <Icon className="size-4 ads:text-icon-default text-gray-10 md:size-3 md:ads:text-icon-emphasis md:text-gray-12" />
            )}
            <div
              className="truncate font-semibold ads:text-metadata text-14 text-gray-10 md:ads:text-primary md:text-12 md:text-gray-12"
              data-testid="FILTER_CARD_TITLE"
            >
              {title}
            </div>
          </div>
          <IconButton appearance="ghost" color="gray" label="Remove filter" size="small" onClick={onFilterDelete}>
            <MinusIcon className="size-4 ads:text-icon-emphasis text-gray-12" />
          </IconButton>
        </div>
        {children}
      </div>
    );
  },
);

FilterCard.displayName = 'FilterCard';
